import { useEffect, useState } from "react"
import styles from "./index.module.css";
import { Grid, Typography, InputBase, IconButton, CircularProgress, Box } from '@material-ui/core';
import RefreshIcon from "@material-ui/icons/Refresh";
import { useSelector } from 'react-redux';
import { IAppState } from '../../../../store';
import { createStyles, withStyles } from "@material-ui/core/styles";
import { useLazyQuery } from '@apollo/client';
import { GET_CONSUMER_DASHBOARD_DATA } from "../../../../graphql/dashboard/getConsumerDashboardDataQuery";
import InvoiceBalanceCard, { IInvoiceBalance } from "./InvoiceBalanceCard";
import { useTranslation } from "react-i18next";
import CreditUtilisation, { ICreditUtilisation } from "./CreditUtilisation";
import MileageOverview, { IMileageOverview } from "./MileageOverview";
import ActiveVehicleCard from "./ActiveVehicleCard";
import BookingStatus, { IBookingStatus } from "./BookingStatus";
import Drivers, { IDrivers } from "./Drivers";
import MOTReminders, { IMOTReminders } from "./MOTReminders";
import ServiceReminders, { IServiceReminders } from "./ServiceReminders";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { formatGraphQLErrorMessage } from "../../../utils";
import { ApolloError } from "apollo-boost";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import VehicleCheckOverviewCard, { IVehicleCheckOverview } from "./VehicleCheckOverviewCard";

interface IDashboardData {
  invoicesBalance: IInvoiceBalance;
  activeBookings: number;
  activeVehicles: number;
  creditUtilisation: ICreditUtilisation;
  mileageOverview: IMileageOverview;
  bookingStatus: IBookingStatus;
  drivers: IDrivers;
  motReminders: IMOTReminders;
  serviceReminders: IServiceReminders;
  vehicleCheckOverview: IVehicleCheckOverview;
}

const defaultDashboardData: IDashboardData = {
  invoicesBalance: {
    totalInvoicesDue: 0,
    totalAmountDue: 0,
    unpaidSundryInvoices: 0,
    sundryInvoiceAmountDue: 0,
  },
  activeBookings: 0,
  activeVehicles: 0,
  creditUtilisation: {
    creditLimit: 0,
    available: 0,
    utilized: 0
  },
  mileageOverview: {
    mileageLimitExited: 0,
    approachingMileageLimits: 0
  },
  bookingStatus: {
    totalVehicles: 0,
    daysInBooking: 0,
    endingThisMonth: 0
  },
  drivers: {
    totalDrivers: 0,
    vehiclesAllocated: 0,
    availableDrivers: 0
  },
  motReminders: {
    upcoming: 0,
    failed: 0
  },
  serviceReminders: {
    upcoming: 0,
    overdue: 0
  },
  vehicleCheckOverview: {
    inProgressBookings: 0,
    completed: 0,
    pending: 0
  }
};

const PortalDashboard = () => {
  const snackbar = useSnackBar();
  const [data, setData] = useState<IDashboardData | null>(defaultDashboardData);
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const { t } = useTranslation();

  const [getDashboardData, { loading }] = useLazyQuery(GET_CONSUMER_DASHBOARD_DATA, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setData(data.consumerDashboardData)
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    },
  });

  const fetchDashboardData = (businessCustomerId?: string) => {
    if (!businessCustomerId) return;
    setData(null);
    getDashboardData({
      variables: { businessCustomerId },
    });
  };

  // auto load when user changes
  useEffect(() => {
    if (user?.businesscustomers?.id) {
      fetchDashboardData(user.businesscustomers.id);
    }
  }, [user]);

  // refresh button
  const refresh = () => {
    if (user?.businesscustomers?.id) {
      fetchDashboardData(user.businesscustomers.id);
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" pb={2}>
        <Typography variant="h2" className="semi-bold">
          {t("root:dashboard")}
        </Typography>
        <IconButton aria-label="settings" onClick={refresh}>
          {loading ? <CircularProgress size={20} thickness={5} /> : <RefreshIcon />}
        </IconButton>
      </Box>
      {data && (
        <Grid container spacing={2}>
          {user?.businesscustomers?.creditLimitEnabled && data.creditUtilisation.creditLimit > 0 &&
            <Grid item xs={12} sm={6} md={4}>
              <CreditUtilisation data={data.creditUtilisation} />
            </Grid>
          }
          <Grid item xs={12} sm={6} md={4}>
            <InvoiceBalanceCard data={data.invoicesBalance} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ActiveVehicleCard data={{ activeBookings: data.activeBookings, activeVehicles: data.activeVehicles }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ServiceReminders data={data.serviceReminders} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <BookingStatus data={data.bookingStatus} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MOTReminders data={data.motReminders} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MileageOverview data={data.mileageOverview} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Drivers data={data.drivers} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <VehicleCheckOverviewCard data={data.vehicleCheckOverview} />
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default PortalDashboard